import FilterForm from '../../../components/forms/FilterForm';
import {
  ASSESSMENT_ATTEMPT_MAP,
  translateAssessmentAttemptStatus
} from '../../../services/assessments';
import { CrudFilters, HttpError } from '@refinedev/core';
import {
  Empty,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Select,
  Space,
  Table
} from 'antd';
import { IAssessmentAttempt } from 'interfaces/assessments';
import useSelectCourseByType from 'hooks/useSelectCourseByType';
import { ListLayout } from 'components/layout/layout/ListLayout';
import {
  ShowButton,
  TagField,
  TextField,
  useTable
} from '@refinedev/antd';
import { formatDate } from '../../../services/date';
import { tagColorByStatus } from '../../../utils/statusColorMapping';
import { useCallback, useEffect, useState } from 'react';

const DEFAULT_PARENT_TYPE = 'Graduate::Discipline';

interface IFilter {
  status: string;
  assessment_name: string;
  student: string;
  email: string;
  parent_id: string;
  parent_type: string;
}

export const AssessmentAttemptsList = () => {
  const [parentType, setParentType] = useState(DEFAULT_PARENT_TYPE);
  const { selectProps, selectedCourseType, setSelectedCourseType } =
    useSelectCourseByType(DEFAULT_PARENT_TYPE, 'graduate_disciplines');

  const { tableProps, searchFormProps } = useTable<
    IAssessmentAttempt,
    HttpError,
    IFilter
  >({
    onSearch: ({
      status,
      assessment_name,
      student,
      email,
      parent_id,
      parent_type
    }) => {
      setParentType(parent_type || DEFAULT_PARENT_TYPE);
      const filters: CrudFilters = [];

      filters.push(
        {
          field: 'filter[by_parent_id]',
          operator: 'eq',
          value: parent_id
        },
        {
          field: 'filter[by_parent_type]',
          operator: 'eq',
          value: parent_type
        },
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: status
        },
        {
          field: 'filter[search_name]',
          operator: 'eq',
          value: assessment_name
        },
        {
          field: 'filter[student]',
          operator: 'eq',
          value: student
        },
        {
          field: 'filter[email]',
          operator: 'eq',
          value: email
        }
      );

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'filter[by_parent_type]',
          operator: 'eq',
          value: 'Graduate::Discipline'
        }
      ]
    }
  });

  const watchedParentType = Form.useWatch('parent_type', searchFormProps.form);

  useEffect(() => {
    if (
      watchedParentType === 'Course' ||
      watchedParentType === 'Graduate::Discipline'
    ) {
      setSelectedCourseType(watchedParentType);
    }
  }, [watchedParentType, setSelectedCourseType]);

  const onChangeType = useCallback(
    (e: RadioChangeEvent) => {
      if (searchFormProps.form?.getFieldValue('parent_id')) {
        searchFormProps.form.setFieldsValue({ parent_id: undefined });
      }

      setSelectedCourseType(e.target.value);
    },
    [searchFormProps.form, setSelectedCourseType]
  );

  return (
    <ListLayout
      title="Histórico de Avaliações"
      createButtonProps={{
        hidden: true
      }}
    >
      <FilterForm searchFormProps={searchFormProps}>
        <Space className="mb-0 bg-slate-100 rounded-md p-2">
          <Space align="center" size="small">
            <span className="text-gray-700">Tipo de curso:</span>
            <Form.Item
              name="parent_type"
              className="mb-0"
              initialValue={DEFAULT_PARENT_TYPE}
            >
              <Radio.Group onChange={onChangeType}>
                <Radio.Button value="Course">Curso</Radio.Button>
                <Radio.Button value="Graduate::Discipline">
                  Disciplina
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Space>

          <Form.Item name="parent_id" className="mb-0 w-60">
            <Select
              key={selectedCourseType}
              placeholder={
                selectedCourseType === 'Course'
                  ? 'Busque por curso'
                  : 'Busque por disciplina'
              }
              allowClear
              {...selectProps}
              notFoundContent={
                <Empty
                  description={`${
                    selectedCourseType === 'Course'
                      ? 'Nenhum curso encontrado'
                      : 'Nenhuma disciplina encontrada'
                  }. Tente refazer sua busca com outros termos`}
                />
              }
            />
          </Form.Item>
        </Space>

        <Form.Item name="assessment_name" className="mb-0 w-64">
          <Input placeholder="Nome da avaliação" />
        </Form.Item>

        <Form.Item name="student" className="mb-0 w-64">
          <Input placeholder="Nome do aluno" />
        </Form.Item>

        <Form.Item name="email" className="mb-0 w-64">
          <Input placeholder="E-mail do aluno" />
        </Form.Item>

        <Form.Item name="status" className="mb-0 w-44" initialValue={null}>
          <Select placeholder="Status da avaliação" allowClear>
            {[...ASSESSMENT_ATTEMPT_MAP].map(([key, value]) => (
              <Select.Option key={key} value={key}>
                {value}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </FilterForm>

      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="assessment"
          key="assessment"
          title="Nome da Avaliação"
          render={(assessment: IAssessmentAttempt['assessment']) => (
            <TextField value={assessment?.name} />
          )}
        />
        <Table.Column
          dataIndex={['assessment', 'course_name']}
          key="course_name"
          title={
            parentType === 'Course' ? 'Nome do Curso' : 'Nome da Disciplina'
          }
          render={(courseName: IAssessmentAttempt['assessment']['course_name']) => (
            <TextField value={courseName ?? '--'} />
          )}
        />
        <Table.Column
          dataIndex="user"
          key="user"
          title="Aluno"
          render={(user: IAssessmentAttempt['user']) => (
            <Space direction="vertical" size={0}>
              <TextField value={user?.full_name_or_name ?? '--'} />
              {user?.email && (
                <TextField
                  style={{ fontSize: 12, color: '#8c8c8c' }}
                  value={`E-mail: ${user.email}`}
                />
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title="Status"
          render={(status: IAssessmentAttempt['status']) => (
            <TagField
              value={translateAssessmentAttemptStatus(status)}
              color={tagColorByStatus(status)}
            />
          )}
        />
        <Table.Column
          dataIndex="created_at"
          key="created_at"
          title="Data de Início"
          width={150}
          render={(createdAt: IAssessmentAttempt['created_at']) => (
            <TextField value={formatDate(createdAt, 'DD/MM/YYYY HH:mm')} />
          )}
        />
        <Table.Column
          dataIndex="completed_at"
          key="completed_at"
          title="Data de Término"
          width={150}
          render={(completedAt: IAssessmentAttempt['completed_at']) => (
            <TextField value={formatDate(completedAt, 'DD/MM/YYYY HH:mm')} />
          )}
        />
        <Table.Column<IAssessmentAttempt>
          title="Ações"
          dataIndex="actions"
          align="center"
          width={80}
          render={(_, record) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </ListLayout>
  );
};
